import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Form, InputGroup, Modal, ProgressBar, Row } from "react-bootstrap";
import { apiGet, apiSendForm, isVideoCategory, MEDIA_CATEGORIES } from "./contentApi";

const fileExtensionFor = (category) => (isVideoCategory(category) ? ".mp4" : ".mp3");

// Texts such as Everyday English dialogues contain tab and line-break
// characters; the editor shows them as \t and \n so they stay visible.
const toEditableText = (text) => (text || "").replace(/\r?\n/g, "\\n").replace(/\t/g, "\\t");
const fromEditableText = (text) => text.replace(/\\n/g, "\n").replace(/\\t/g, "\t");

/**
 * Create (item = null) or edit a media item. `defaults` pre-fills level, type
 * and unit from the current filters when creating.
 */
const MediaItemFormModal = ({ show, item, defaults, levels, onClose, onSaved }) => {
    const [levelId, setLevelId] = useState("");
    const [category, setCategory] = useState(MEDIA_CATEGORIES[0].value);
    const [unitId, setUnitId] = useState("");
    const [units, setUnits] = useState([]);
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [screenOrder, setScreenOrder] = useState("");
    const [isGroupTitle, setIsGroupTitle] = useState(false);
    const [file, setFile] = useState(null);
    const [urlText, setUrlText] = useState("");
    const [progress, setProgress] = useState(null);
    const [error, setError] = useState("");

    const isEdit = Boolean(item);
    const saving = progress !== null;

    useEffect(() => {
        if (!show) return;
        setLevelId(String(item?.levelId ?? defaults.levelId ?? levels[0]?.id ?? ""));
        setCategory(item?.category ?? defaults.category ?? MEDIA_CATEGORIES[0].value);
        setUnitId(String(item?.unitId ?? defaults.unitId ?? ""));
        setTitle(toEditableText(item?.title));
        setBody(toEditableText(item?.body));
        setScreenOrder(item?.screenOrder != null ? String(item.screenOrder) : "");
        setIsGroupTitle(Boolean(item?.isGroupTitle));
        setFile(null);
        setUrlText(item?.url ?? "");
        setProgress(null);
        setError("");
    }, [show, item, defaults, levels]);

    useEffect(() => {
        if (!show || !levelId) return;
        apiGet(`/levels/${levelId}/units`)
            .then((data) => {
                const loaded = data || [];
                setUnits(loaded);
                setUnitId((current) =>
                    loaded.some((u) => String(u.id) === current) ? current : String(loaded[0]?.id ?? "")
                );
            })
            .catch(() => setUnits([]));
    }, [show, levelId]);

    const extension = fileExtensionFor(category);
    const fileTypeMatches = !file || file.name.toLowerCase().endsWith(extension);
    // File name on the server, without the extension (the item's "url").
    const originalUrl = item?.url ?? "";
    const typedUrl = urlText.trim().replace(/\.(mp3|mp4)$/i, "");
    const urlChanged = typedUrl !== originalUrl;
    const detaching = isEdit && Boolean(originalUrl) && !typedUrl && !file;
    const linkingExisting = !file && Boolean(typedUrl) && urlChanged;
    const keepingCurrent = isEdit && !file && Boolean(typedUrl) && !urlChanged;
    const fileTypeChanged = keepingCurrent && fileExtensionFor(item.category) !== extension;

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError("");

        if (!title.trim()) return setError("Please enter a title.");
        if (!unitId) return setError("Please choose a unit.");
        if (!isGroupTitle && !isEdit && !file && !typedUrl) {
            return setError("Choose a file to upload or enter the name of an existing file.");
        }
        if (!isGroupTitle && fileTypeChanged) {
            return setError(`This type needs a ${extension} file: upload one or enter the name of an existing ${extension} file.`);
        }
        if (!fileTypeMatches) return setError(`This type needs a ${extension} file.`);

        const formData = new FormData();
        formData.append("levelId", levelId);
        formData.append("unitId", unitId);
        formData.append("category", category);
        formData.append("title", fromEditableText(title.trim()));
        formData.append("body", fromEditableText(body));
        formData.append("isGroupTitle", isGroupTitle);
        if (!file && (isEdit || typedUrl)) formData.append("url", typedUrl);
        if (screenOrder !== "") formData.append("screenOrder", screenOrder);
        if (file && !isGroupTitle) formData.append("file", file);

        try {
            setProgress(0);
            const path = isEdit ? `/admin/content/media-items/${item.id}` : "/admin/content/media-items";
            const saved = await apiSendForm(path, formData, setProgress);
            onSaved(saved);
        } catch (err) {
            setError(err.message);
            setProgress(null);
        }
    };

    return (
        <Modal show={show} onHide={saving ? undefined : onClose} size="lg" centered backdrop={saving ? "static" : true}>
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton={!saving}>
                    <Modal.Title className="fs-5">{isEdit ? "Edit media item" : "Add media item"}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}

                    <Row className="g-2 mb-3">
                        <Col md={4}>
                            <Form.Label className="small text-muted mb-1">Level</Form.Label>
                            <Form.Select value={levelId} onChange={(e) => setLevelId(e.target.value)} disabled={saving}>
                                {levels.map((level) => (
                                    <option key={level.id} value={level.id}>{level.title}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col md={4}>
                            <Form.Label className="small text-muted mb-1">Type</Form.Label>
                            <Form.Select value={category} onChange={(e) => setCategory(e.target.value)} disabled={saving}>
                                {MEDIA_CATEGORIES.map((c) => (
                                    <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col md={4}>
                            <Form.Label className="small text-muted mb-1">Unit</Form.Label>
                            <Form.Select value={unitId} onChange={(e) => setUnitId(e.target.value)} disabled={saving}>
                                {units.map((unit) => (
                                    <option key={unit.id} value={unit.id}>{unit.screenOrder}. {unit.title}</option>
                                ))}
                            </Form.Select>
                        </Col>
                    </Row>

                    <Form.Check
                        type="switch"
                        id="media-is-group-title"
                        className="mb-3"
                        label="Group title (a section header in the list, without a file)"
                        checked={isGroupTitle}
                        onChange={(e) => setIsGroupTitle(e.target.checked)}
                        disabled={saving}
                    />

                    <Row className="g-2 mb-3">
                        <Col md={9}>
                            <Form.Label className="small text-muted mb-1">Title</Form.Label>
                            <Form.Control
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                disabled={saving}
                                className="font-monospace small"
                            />
                            <Form.Text muted>
                                Use <code>\n</code> for a new line and <code>\t</code> for a tab,
                                e.g. <code>A:\tGood night!\nB:\tGood night! Sleep well!</code>
                            </Form.Text>
                            {/\\[nt]/.test(title) && (
                                <div
                                    className="mt-2 p-2 rounded bg-body-tertiary small"
                                    style={{ whiteSpace: "pre-wrap", tabSize: 4 }}
                                >
                                    {fromEditableText(title)}
                                </div>
                            )}
                        </Col>
                        <Col md={3}>
                            <Form.Label className="small text-muted mb-1">Order</Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                placeholder={isEdit ? "" : "Auto (last)"}
                                value={screenOrder}
                                onChange={(e) => setScreenOrder(e.target.value)}
                                disabled={saving}
                            />
                        </Col>
                    </Row>

                    {!isGroupTitle && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label className="small text-muted mb-1">Text / transcript (optional)</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    disabled={saving}
                                    className="font-monospace small"
                                />
                                <Form.Text muted>
                                    Use <code>\n</code> for a new line and <code>\t</code> for a tab,
                                    e.g. <code>A:\tGood night!\nB:\tGood night! Sleep well!</code>
                                </Form.Text>

                                {body.trim() && (
                                    <div className="mt-2">
                                        <div className="small text-muted mb-1">Preview</div>
                                        <div
                                            className="p-2 rounded bg-body-tertiary small"
                                            style={{ whiteSpace: "pre-wrap", tabSize: 4 }}
                                        >
                                            {fromEditableText(body)}
                                        </div>
                                    </div>
                                )}
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="small text-muted mb-1">File name on server</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        className="font-monospace small"
                                        placeholder={isEdit ? "No file attached" : "e.g. lesson_1 (optional when uploading)"}
                                        value={file ? "" : urlText}
                                        onChange={(e) => setUrlText(e.target.value)}
                                        disabled={saving || Boolean(file)}
                                    />
                                    <InputGroup.Text>{extension}</InputGroup.Text>
                                    {isEdit && originalUrl && typedUrl && !file && (
                                        <Button variant="outline-warning" onClick={() => setUrlText("")} disabled={saving}>
                                            Detach file
                                        </Button>
                                    )}
                                    {isEdit && urlChanged && !file && (
                                        <Button variant="outline-secondary" onClick={() => setUrlText(originalUrl)} disabled={saving}>
                                            Undo
                                        </Button>
                                    )}
                                </InputGroup>

                                <div className="small mt-2">
                                    {file ? (
                                        <span className="text-muted">The uploaded file is saved under its own name.</span>
                                    ) : detaching ? (
                                        <Alert variant="warning" className="py-2 mb-0">
                                            The file will be detached when you save. It stays on the server,
                                            but this item won't play in the app until a file is attached.
                                        </Alert>
                                    ) : linkingExisting ? (
                                        <span className="text-muted">
                                            The item will be linked to <code>{typedUrl}{extension}</code>, which must already
                                            exist in this level's and type's folder (see <b>Check files</b>).
                                            {isEdit && originalUrl && " The previous file stays on the server."}
                                        </span>
                                    ) : keepingCurrent ? (
                                        <span className="text-muted">
                                            Current file.
                                            {fileTypeChanged && ` This type needs a ${extension} file: upload one or enter another name.`}
                                        </span>
                                    ) : (
                                        <span className="text-muted">
                                            Enter the name of a file that is already on the server, or upload one below.
                                        </span>
                                    )}
                                </div>
                            </Form.Group>

                            <Form.Group>
                                <Form.Label className="small text-muted mb-1">
                                    {keepingCurrent ? `Replace file (${extension}, optional)` : `Upload file (${extension})`}
                                </Form.Label>
                                <Form.Control
                                    type="file"
                                    accept={isVideoCategory(category) ? ".mp4,video/mp4" : ".mp3,audio/mpeg"}
                                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                                    isInvalid={!fileTypeMatches}
                                    disabled={saving}
                                />
                                <Form.Control.Feedback type="invalid">
                                    This type needs a {extension} file.
                                </Form.Control.Feedback>
                            </Form.Group>
                        </>
                    )}

                    {saving && (
                        <ProgressBar
                            className="mt-3"
                            now={progress}
                            label={progress < 100 ? `${progress}%` : "Saving…"}
                            animated={progress >= 100}
                        />
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button type="submit" variant="primary" disabled={saving || !fileTypeMatches}>
                        {isEdit ? "Save changes" : "Add item"}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default MediaItemFormModal;
